import { platform } from "@/platform/runtime";
/**
 * Tipi onboarding SaaS (tenant_onboardings / cgs_versions)
 * usati finché types.ts non è rigenerato.
 */

export type OnboardingStatus = "in_corso" | "completato" | "annullato" | "scaduto";

export type OnboardingStep =
  | "setup"
  | "terms"
  | "payment"
  | "access"
  | "call"
  | "done";

export type OnboardingBillingPeriod = "monthly" | "yearly";

/** Metodo pagamento onboarding (scelto in Core) */
export type OnboardingPaymentMethod = "carta" | "bonifico" | "entrambi";

export type OnboardingUnlockMode = "existing_trial" | "new_access";

export type OnboardingCallStatus = "pending" | "booked" | "skipped";

export interface TenantOnboarding {
  id: string;
  tenant_id: string;
  signer_user_id: string;
  token: string;
  status: OnboardingStatus;
  current_step: OnboardingStep;
  price_amount: number;
  /** Installazione una tantum (accanto al ricorrente) */
  setup_fee_amount: number;
  billing_period: OnboardingBillingPeriod;
  /** Anni di fatturazione se yearly (1–10) */
  billing_years?: number;
  /** Metodo pagamento: carta | bonifico | entrambi */
  payment_method?: OnboardingPaymentMethod;
  currency: string;
  notes_internal: string | null;
  expires_at: string;
  created_by: string | null;
  economic_sheet_snapshot: Record<string, unknown> | null;
  cgs_version_id: string | null;
  cgs_version_label: string | null;
  cgs_content_hash: string | null;
  terms_accepted_at: string | null;
  terms_ip: string | null;
  terms_user_agent: string | null;
  flag_cgs_accepted: boolean;
  flag_art50_accepted: boolean;
  contract_pdf_path: string | null;
  contract_email_sent_at: string | null;
  contract_email_error: string | null;
  whop_checkout_id: string | null;
  whop_plan_id: string | null;
  whop_payment_id: string | null;
  whop_membership_id: string | null;
  payment_succeeded_at: string | null;
  payment_amount: number | null;
  /** Bonifico installazione registrato come ricevuto dal Core */
  setup_fee_received_at: string | null;
  unlock_mode: OnboardingUnlockMode | null;
  call_status: OnboardingCallStatus | null;
  call_booked_at: string | null;
  call_scheduled_at: string | null;
  call_calendar_url_snapshot: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  /** Join opzionale lista / dettaglio */
  tenant?: {
    id: string;
    name: string;
    whop_environment?: string | null;
    whop_product_id?: string | null;
    whop_plan_id?: string | null;
    whop_renewal_price?: number | null;
  } | null;
}

export const ONBOARDING_STATUS_LABELS: Record<OnboardingStatus, string> = {
  in_corso: "In corso",
  completato: "Completato",
  annullato: "Annullato",
  scaduto: "Scaduto",
};

export const ONBOARDING_STEP_LABELS: Record<OnboardingStep, string> = {
  setup: "Setup",
  terms: "Termini e contratto",
  payment: "Pagamento",
  access: "Accesso",
  call: "Call onboarding",
  done: "Completato",
};

export const ONBOARDING_CALL_STATUS_LABELS: Record<OnboardingCallStatus, string> = {
  pending: "In attesa",
  booked: "Prenotata",
  skipped: "Saltata (pending)",
};

export const ONBOARDING_BILLING_LABELS: Record<OnboardingBillingPeriod, string> = {
  monthly: "Mensile",
  yearly: "Annuale",
};

export const ONBOARDING_PAYMENT_METHOD_LABELS: Record<
  OnboardingPaymentMethod,
  string
> = {
  carta: "Solo carta (Whop)",
  bonifico: "Solo bonifico",
  entrambi: "Carta + bonifico installazione",
};

/** Normalizza periodo legacy biennial/triennial → yearly + anni. */
export function resolveOnboardingBilling(
  period?: string | null,
  years?: number | null,
): { period: OnboardingBillingPeriod; years: number } {
  if (period === "biennial") return { period: "yearly", years: 2 };
  if (period === "triennial") return { period: "yearly", years: 3 };
  if (period === "yearly") {
    const y = Math.max(1, Math.min(10, Number(years) || 1));
    return { period: "yearly", years: y };
  }
  return { period: "monthly", years: 1 };
}

/** Normalizza metodo pagamento (default entrambi). */
export function resolveOnboardingPaymentMethod(
  raw?: string | null,
): OnboardingPaymentMethod {
  if (raw === "carta" || raw === "bonifico" || raw === "entrambi") return raw;
  return "entrambi";
}

/** True se serve conferma bonifico Core. */
export function onboardingNeedsBankConfirm(row: {
  payment_method?: string | null;
  setup_fee_amount?: number | null;
}): boolean {
  const method = resolveOnboardingPaymentMethod(row.payment_method);
  if (method === "bonifico") return true;
  if (method === "carta") return false;
  return (Number(row.setup_fee_amount ?? 0) || 0) > 0;
}

/** Parti pagamento da mostrare nel tracker (carta / bonifico). */
export function onboardingPaymentTrackerParts(row: {
  payment_method?: string | null;
  setup_fee_amount?: number | null;
}): Array<"card" | "bank"> {
  const method = resolveOnboardingPaymentMethod(row.payment_method);
  if (method === "carta") return ["card"];
  if (method === "bonifico") return ["bank"];
  if (onboardingNeedsBankConfirm(row)) return ["card", "bank"];
  return ["card"];
}

/** Giorni billing period per plan Whop */
export function onboardingBillingPeriodDays(
  period: OnboardingBillingPeriod | string,
  years?: number | null,
): number {
  const resolved = resolveOnboardingBilling(period, years);
  if (resolved.period === "monthly") return 30;
  return resolved.years * 365;
}

/** Suffisso prezzo: / mese | / anno | / N anni */
export function onboardingBillingPeriodSuffix(
  period: OnboardingBillingPeriod | string,
  years?: number | null,
): string {
  const resolved = resolveOnboardingBilling(period, years);
  if (resolved.period === "monthly") return "/ mese";
  if (resolved.years <= 1) return "/ anno";
  return `/ ${resolved.years} anni`;
}

/** Label periodità per UI (es. Mensile, Annuale 2 anni) */
export function onboardingBillingPeriodLabel(
  period: OnboardingBillingPeriod | string,
  years?: number | null,
): string {
  const resolved = resolveOnboardingBilling(period, years);
  if (resolved.period === "monthly") return "Mensile";
  if (resolved.years <= 1) return "Annuale";
  return `Annuale (${resolved.years} anni)`;
}

/** Formatta prezzo ricorrente + periodo per UI Core */
export function formatOnboardingPrice(
  amount: number,
  period: OnboardingBillingPeriod | string,
  currency = "eur",
  years?: number | null,
): string {
  const formatted = new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amount));
  return `${formatted} ${onboardingBillingPeriodSuffix(period, years)}`;
}

/** Formatta solo un importo in valuta */
export function formatOnboardingMoney(amount: number, currency = "eur"): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amount) || 0);
}

/**
 * Riepilogo condizioni economiche: ricorrente + installazione una tantum (se > 0).
 * Es. "247,00 € / mese + 500,00 € installazione una tantum"
 */
export const ONBOARDING_SETUP_FEE_LABEL = "installazione una tantum";

export function formatOnboardingEconomicSummary(
  recurringAmount: number,
  period: OnboardingBillingPeriod | string,
  setupFeeAmount: number,
  currency = "eur",
  years?: number | null,
): string {
  const recurring = formatOnboardingPrice(recurringAmount, period, currency, years);
  const fee = Number(setupFeeAmount) || 0;
  if (fee <= 0) return recurring;
  return `${recurring} + ${formatOnboardingMoney(fee, currency)} ${ONBOARDING_SETUP_FEE_LABEL}`;
}

/** Path pubblici del funnel cliente (senza origin) */
export function onboardingPublicPath(
  token: string,
  step: "entry" | "terms" | "payment" | "call" | "completed",
): string {
  if (step === "entry") return `/onboarding/${token}`;
  if (step === "terms") return `/onboarding/${token}/termini`;
  if (step === "payment") return `/onboarding/${token}/pagamento`;
  if (step === "call") return `/onboarding/${token}/call`;
  return `/onboarding/${token}/completato`;
}

export function onboardingPublicUrl(
  token: string,
  step: "entry" | "terms" | "payment" | "call" | "completed",
): string {
  // Il funnel è servito dal dominio del prodotto venduto (Construction), non dalla Console
  return `${platform.originFor("construction")}${onboardingPublicPath(token, step)}`;
}

export type TrackerStepState = "done" | "current" | "todo";

export type OnboardingTrackerDisplayKey =
  | OnboardingStep
  | "payment_card"
  | "payment_bank";

export type OnboardingTrackerDisplayStep = {
  key: OnboardingTrackerDisplayKey;
  label: string;
  description: string;
  linkStep: "entry" | "terms" | "payment" | "call" | "completed" | "login" | null;
  state: TrackerStepState;
};

/** Stato generico lungo la sequenza DB (senza split pagamento). */
export function getTrackerStepState(
  stepKey: OnboardingStep,
  currentStep: OnboardingStep,
  status: OnboardingStatus,
): TrackerStepState {
  const order: OnboardingStep[] = [
    "setup",
    "terms",
    "payment",
    "access",
    "call",
    "done",
  ];
  if (stepKey === "setup") return "done";

  if (status === "completato" && currentStep === "call") {
    const accessIdx = order.indexOf("access");
    const stepIdx = order.indexOf(stepKey);
    if (stepIdx <= accessIdx) return "done";
    if (stepKey === "call") return "current";
    return "todo";
  }

  if (status === "completato" && (currentStep === "done" || currentStep === "access")) {
    return "done";
  }

  const curIdx = order.indexOf(currentStep);
  const stepIdx = order.indexOf(stepKey);
  if (stepIdx < curIdx) return "done";
  if (stepIdx === curIdx) return "current";
  return "todo";
}

/**
 * Tracker Core: split pagamento in carta / bonifico in base al metodo.
 * - carta → solo step carta
 * - bonifico → solo step bonifico
 * - entrambi (+ fee) → carta poi bonifico (ok / pending)
 */
export function buildOnboardingTrackerSteps(row: {
  status: OnboardingStatus;
  current_step: OnboardingStep;
  payment_method?: string | null;
  setup_fee_amount?: number | null;
  payment_succeeded_at?: string | null;
  setup_fee_received_at?: string | null;
}): OnboardingTrackerDisplayStep[] {
  const paymentParts = onboardingPaymentTrackerParts(row);
  const basePaymentState = getTrackerStepState("payment", row.current_step, row.status);
  const cardDone = !!row.payment_succeeded_at;
  const bankDone = !!row.setup_fee_received_at;
  const pastPayment =
    basePaymentState === "done" ||
    row.status === "completato" ||
    ["access", "call", "done"].includes(row.current_step);

  const paymentSteps: OnboardingTrackerDisplayStep[] = [];

  if (paymentParts.includes("card")) {
    let state: TrackerStepState = "todo";
    if (pastPayment || cardDone) state = "done";
    else if (basePaymentState === "current" && !cardDone) state = "current";
    else if (basePaymentState === "current" && cardDone && paymentParts.includes("bank")) {
      state = "done";
    }
    paymentSteps.push({
      key: "payment_card",
      label: "Pagamento con carta",
      description: cardDone
        ? "Canone Whop confermato"
        : basePaymentState === "current"
          ? "In attesa pagamento carta"
          : "Canone ricorrente via Whop",
      linkStep: "payment",
      state,
    });
  }

  if (paymentParts.includes("bank")) {
    let state: TrackerStepState = "todo";
    if (pastPayment || bankDone) state = "done";
    else if (basePaymentState === "current") {
      if (paymentParts.includes("card") && !cardDone) state = "todo";
      else state = "current"; // pending bonifico
    }
    paymentSteps.push({
      key: "payment_bank",
      label: "Pagamento con bonifico",
      description: bankDone
        ? "Bonifico confermato in Core"
        : state === "current"
          ? "Pending conferma bonifico"
          : paymentParts.includes("card")
            ? "Dopo la carta: installazione / totale via bonifico"
            : "Bonifico totale (canone + installazione)",
      linkStep: "payment",
      state,
    });
  }

  const steps: OnboardingTrackerDisplayStep[] = [
    {
      key: "setup",
      label: "Setup",
      description: "Pricing e firmatario configurati in Core",
      linkStep: null,
      state: "done",
    },
    {
      key: "terms",
      label: "Termini e contratto",
      description: "SCE, CGS, flag art. 50 e conferma attivazione",
      linkStep: "terms",
      state: getTrackerStepState("terms", row.current_step, row.status),
    },
    ...paymentSteps,
    {
      key: "access",
      label: "Accesso",
      description: "Sblocco login (trial illimitato o nuove credenziali)",
      linkStep: "login",
      state: getTrackerStepState("access", row.current_step, row.status),
    },
    {
      key: "call",
      label: "Call onboarding",
      description: "Prenota dal calendario o salta (resta pending)",
      linkStep: "call",
      state: getTrackerStepState("call", row.current_step, row.status),
    },
    {
      key: "done",
      label: "Completato",
      description: "Funnel chiuso",
      linkStep: null,
      state: getTrackerStepState("done", row.current_step, row.status),
    },
  ];

  return steps;
}

/** @deprecated preferisci buildOnboardingTrackerSteps */
export const ONBOARDING_TRACKER_STEPS: {
  key: OnboardingStep;
  label: string;
  description: string;
  linkStep: "entry" | "terms" | "payment" | "call" | "completed" | "login" | null;
}[] = [
  {
    key: "setup",
    label: "Setup",
    description: "Pricing e firmatario configurati in Core",
    linkStep: null,
  },
  {
    key: "terms",
    label: "Termini e contratto",
    description: "SCE, CGS, flag art. 50 e conferma attivazione",
    linkStep: "terms",
  },
  {
    key: "payment",
    label: "Pagamento",
    description: "Canone e/o bonifico secondo metodo pagamento",
    linkStep: "payment",
  },
  {
    key: "access",
    label: "Accesso",
    description: "Sblocco login (trial illimitato o nuove credenziali)",
    linkStep: "login",
  },
  {
    key: "call",
    label: "Call onboarding",
    description: "Prenota dal calendario o salta (resta pending)",
    linkStep: "call",
  },
  {
    key: "done",
    label: "Completato",
    description: "Funnel chiuso",
    linkStep: null,
  },
];
